import React, { useEffect, useState } from 'react';
import { studentAPI, paymentAPI, complaintAPI, visitorAPI, bankDetailAPI, paymentSlipAPI } from '../../services/api';
import { Card, StatCard, Badge, Loading, Button } from '../../components/common';
import { useAuth } from '../../context/AuthContext';
import { getPeriodShortLabel } from '../../utils/paymentUtils';
import { DoorOpen, CreditCard, AlertCircle, UserCheck, Wifi, Zap, Droplets, Wrench, Copy, Check, Upload, Download, File } from 'lucide-react';
import { Link } from 'react-router-dom';
import toast from 'react-hot-toast';

export default function StudentDashboard() {
  const { user } = useAuth();
  const [student, setStudent] = useState(null);
  const [payments, setPayments] = useState([]);
  const [slips, setSlips] = useState([]);
  const [bankDetails, setBankDetails] = useState(null);
  const [complaints, setComplaints] = useState([]);
  const [visitors, setVisitors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [copiedField, setCopiedField] = useState(null);
  const [selectedPaymentForSlip, setSelectedPaymentForSlip] = useState(null);

  const loadData = async () => {
    Promise.all([
      studentAPI.getMe().catch(() => null),
      paymentAPI.getAll(),
      complaintAPI.getAll(),
      visitorAPI.getAll(),
      bankDetailAPI.getAll().catch(() => null),
      paymentSlipAPI.getSlips().catch(() => ({ data: { data: [] } })),
    ]).then(([s, p, c, v, b, slipsData]) => {
      setStudent(s?.data?.data);
      setPayments(p.data.data);
      setComplaints(c.data.data);
      setVisitors(v.data.data);
      setSlips(slipsData.data.data || []);
      if (b?.data?.data?.length > 0) {
        setBankDetails(b.data.data[0]);
      }
    }).finally(() => setLoading(false));
  };

  useEffect(() => {
    loadData();
  }, []);

  const handleCopyToClipboard = (text, field) => {
    navigator.clipboard.writeText(text);
    setCopiedField(field);
    toast.success('Copied to clipboard');
    setTimeout(() => setCopiedField(null), 2000);
  };

  const handleSlipUpload = async (e, paymentId) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const allowedTypes = ['application/pdf', 'image/jpeg', 'image/png'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Only PDF, JPG, PNG files allowed');
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      toast.error('File size must be less than 5MB');
      return;
    }

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('paymentId', paymentId);

      const { data } = await paymentSlipAPI.upload(formData);
      toast.success('Payment slip uploaded successfully');
      setSlips([data.data, ...slips]);
      setSelectedPaymentForSlip(null);
    } catch (error) {
      toast.error(error.response?.data?.message || 'Error uploading slip');
    } finally {
      setUploading(false);
    }
  };

  if (loading) return <Loading />;

  const pendingPayments = payments.filter(p => p.status === 'pending').length;
  const activeComplaints = complaints.filter(c => c.status !== 'resolved' && c.status !== 'closed').length;
  const amountPaid = payments.filter(p => p.status === 'paid').reduce((sum, p) => sum + (p.amount || 0), 0);

  return (
    <div className="space-y-8">
      {/* Welcome banner */}
      <div className="bg-gradient-to-r from-emerald-600 to-teal-700 rounded-2xl p-6 text-white">
        <h2 className="font-display text-2xl font-bold">Hello, {user?.name?.split(' ')[0]}! 👋</h2>
        <p className="text-emerald-100 mt-1 text-sm">Here's your hostel status overview</p>

        {/* Room info */}
        {student?.room ? (
          <div className="flex flex-wrap gap-4 mt-4">
            <div className="bg-white/15 rounded-xl px-4 py-2.5">
              <div className="text-sm font-semibold">Room {student.room.roomNumber}</div>
              <div className="text-xs text-emerald-100">Block {student.room.hostelBlock}</div>
            </div>
            <div className="bg-white/15 rounded-xl px-4 py-2.5">
              <div className="text-sm font-semibold">Floor {student.room.floor}</div>
              <div className="text-xs text-emerald-100">{student.room.type} room</div>
            </div>
            <div className="bg-white/15 rounded-xl px-4 py-2.5">
              <div className="text-sm font-semibold">₹{student.room.monthlyRent?.toLocaleString('en-IN')}/mo</div>
              <div className="text-xs text-emerald-100">Monthly rent</div>
            </div>
          </div>
        ) : (
          <div className="mt-4 bg-white/15 rounded-xl px-4 py-3 flex items-center justify-between">
            <span className="text-sm">⚠️ No room assigned yet.</span>
            <Link to="/student/browse-rooms" className="text-sm font-semibold text-white hover:underline">Browse rooms →</Link>
          </div>
        )}
      </div>

      {/* Quick stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard title="Pending Payments" value={pendingPayments} icon={CreditCard} color={pendingPayments > 0 ? 'orange' : 'green'} subtitle="Need payment" />
        <StatCard title="Amount Paid" value={`₹${(amountPaid / 1000).toFixed(0)}K`} icon={CreditCard} color="green" subtitle="Total paid" />
        <StatCard title="Active Complaints" value={activeComplaints} icon={AlertCircle} color={activeComplaints > 0 ? 'red' : 'green'} subtitle="Open tickets" />
        <StatCard title="Total Complaints" value={complaints.length} icon={AlertCircle} color="purple" subtitle="All time" />
      </div>

      {/* Quick actions */}
      <div>
        <h3 className="font-display font-semibold text-slate-900 mb-3">Quick Actions</h3>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          {[
            { to: '/student/complaints', icon: Zap, label: 'Electricity Issue', color: 'bg-amber-50 text-amber-600 border-amber-100' },
            { to: '/student/complaints', icon: Droplets, label: 'Water Problem', color: 'bg-blue-50 text-blue-600 border-blue-100' },
            { to: '/student/complaints', icon: Wifi, label: 'WiFi Issue', color: 'bg-violet-50 text-violet-600 border-violet-100' },
            { to: '/student/visitors', icon: UserCheck, label: 'Register Visitor', color: 'bg-emerald-50 text-emerald-600 border-emerald-100' },
          ].map(a => (
            <Link key={a.label} to={a.to} className={`flex flex-col items-center gap-2 p-4 rounded-2xl border ${a.color} hover:opacity-80 transition text-center`}>
              <a.icon className="w-6 h-6" />
              <span className="text-xs font-medium">{a.label}</span>
            </Link>
          ))}
        </div>
      </div>

      {/* Recent activity */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card className="p-6">
          <div className="flex justify-between items-center mb-4">
            <h3 className="font-display font-semibold text-slate-900">Payment Details</h3>
            <Link to="/student/payments" className="text-xs text-primary-600 hover:underline font-medium">View all</Link>
          </div>
          
          {/* Bank Details Section */}
          {bankDetails && payments.some(p => p.status === 'pending') && (
            <div className="mb-5 p-4 bg-emerald-50 rounded-xl border border-emerald-200">
              <p className="text-xs font-semibold text-emerald-800 uppercase mb-3">Bank Transfer Details</p>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between items-center py-2 border-b border-emerald-100">
                  <span className="text-slate-600">Account Holder</span>
                  <div className="flex items-center gap-2">
                    <span className="font-medium text-slate-900">{bankDetails.accountHolderName}</span>
                    <button
                      onClick={() => handleCopyToClipboard(bankDetails.accountHolderName, 'holder')}
                      className="p-1 hover:bg-emerald-200 rounded"
                    >
                      {copiedField === 'holder' ? <Check className="w-4 h-4 text-emerald-600" /> : <Copy className="w-4 h-4 text-slate-400" />}
                    </button>
                  </div>
                </div>
                <div className="flex justify-between items-center py-2 border-b border-emerald-100">
                  <span className="text-slate-600">Account No.</span>
                  <div className="flex items-center gap-2">
                    <span className="font-medium text-slate-900 font-mono">{bankDetails.accountNumber}</span>
                    <button
                      onClick={() => handleCopyToClipboard(bankDetails.accountNumber, 'account')}
                      className="p-1 hover:bg-emerald-200 rounded"
                    >
                      {copiedField === 'account' ? <Check className="w-4 h-4 text-emerald-600" /> : <Copy className="w-4 h-4 text-slate-400" />}
                    </button>
                  </div>
                </div>
                <div className="flex justify-between items-center py-2 border-b border-emerald-100">
                  <span className="text-slate-600">IFSC Code</span>
                  <div className="flex items-center gap-2">
                    <span className="font-medium text-slate-900 font-mono">{bankDetails.ifscCode}</span>
                    <button
                      onClick={() => handleCopyToClipboard(bankDetails.ifscCode, 'ifsc')}
                      className="p-1 hover:bg-emerald-200 rounded"
                    >
                      {copiedField === 'ifsc' ? <Check className="w-4 h-4 text-emerald-600" /> : <Copy className="w-4 h-4 text-slate-400" />}
                    </button>
                  </div>
                </div>
                <div className="flex justify-between items-center py-2">
                  <span className="text-slate-600">Bank Name</span>
                  <span className="font-medium text-slate-900">{bankDetails.bankName}</span>
                </div>
                {bankDetails.upiId && (
                  <div className="flex justify-between items-center py-2 border-t border-emerald-100">
                    <span className="text-slate-600">UPI ID</span>
                    <div className="flex items-center gap-2">
                      <span className="font-medium text-slate-900 font-mono">{bankDetails.upiId}</span>
                      <button
                        onClick={() => handleCopyToClipboard(bankDetails.upiId, 'upi')}
                        className="p-1 hover:bg-emerald-200 rounded"
                      >
                        {copiedField === 'upi' ? <Check className="w-4 h-4 text-emerald-600" /> : <Copy className="w-4 h-4 text-slate-400" />}
                      </button>
                    </div>
                  </div>
                )}
              </div>
              <p className="text-xs text-emerald-700 mt-3 italic">Please add your roll number as payment reference</p>
            </div>
          )}

          {/* Payment History */}
          {payments.length === 0 ? <p className="text-slate-400 text-sm text-center py-4">No payment records</p> : (
            <div className="space-y-3">
              {payments.slice(0, 4).map(p => {
                const periodLabel = getPeriodShortLabel(p.period);
                return (
                  <div key={p._id} className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-slate-800">{periodLabel} {p.year}</p>
                      <p className="text-xs text-slate-400">{p.paymentDate ? new Date(p.paymentDate).toLocaleDateString('en-IN') : 'Due: ' + (p.dueDate ? new Date(p.dueDate).toLocaleDateString('en-IN') : '—')}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-sm font-semibold">₹{p.amount?.toLocaleString('en-IN')}</p>
                      <Badge status={p.status} />
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </Card>

        <Card className="p-6">
          <div className="flex justify-between items-center mb-4">
            <h3 className="font-display font-semibold text-slate-900">Payment Slips</h3>
            <Link to="/student/payments" className="text-xs text-primary-600 hover:underline font-medium">View all</Link>
          </div>

          {/* Upload Section */}
          {payments.some(p => p.status === 'pending' || p.status === 'paid') && (
            <div className="mb-5 p-4 bg-blue-50 rounded-xl border-2 border-dashed border-blue-200">
              <div className="space-y-3">
                <div>
                  <p className="text-sm font-semibold text-blue-900 mb-2">📄 Upload Payment Slip</p>
                  <p className="text-xs text-blue-700 mb-3">Select a pending payment to upload proof</p>
                </div>
                
                <select
                  value={selectedPaymentForSlip || ''}
                  onChange={(e) => setSelectedPaymentForSlip(e.target.value)}
                  className="w-full border border-blue-300 rounded-lg px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">Select payment without slip...</option>
                  {payments
                    .filter(p => !slips.some(s => s.payment._id === p._id))
                    .map(p => (
                      <option key={p._id} value={p._id}>
                        {getPeriodShortLabel(p.period)} {p.year} - ₹{p.amount?.toLocaleString('en-IN')} ({p.status})
                      </option>
                    ))}
                </select>

                {selectedPaymentForSlip && (
                  <label className="flex items-center justify-center gap-2 px-4 py-3 bg-white border border-blue-300 rounded-lg cursor-pointer hover:bg-blue-50 transition">
                    <Upload className="w-4 h-4 text-blue-600" />
                    <span className="text-sm font-medium text-blue-600">Choose File (PDF, JPG, PNG)</span>
                    <input
                      type="file"
                      accept=".pdf,.jpg,.jpeg,.png"
                      onChange={(e) => handleSlipUpload(e, selectedPaymentForSlip)}
                      disabled={uploading}
                      className="hidden"
                    />
                  </label>
                )}
                {!selectedPaymentForSlip && <p className="text-xs text-blue-600 italic">Select a payment first</p>}
              </div>
            </div>
          )}

          {/* Slip History */}
          {slips.length === 0 ? (
            <p className="text-slate-400 text-sm text-center py-6">No payment slips uploaded yet</p>
          ) : (
            <div className="space-y-3">
              {slips.slice(0, 5).map(slip => (
                <div key={slip._id} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg border border-slate-200 hover:border-slate-300 transition">
                  <div className="flex items-center gap-3 min-w-0">
                    <File className="w-5 h-5 text-slate-400 flex-shrink-0" />
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-slate-800 truncate font-mono">{slip.fileName}</p>
                      <div className="flex gap-2 text-xs text-slate-500">
                        <span>{getPeriodShortLabel(slip.payment.period)} {slip.payment.year}</span>
                        <span>•</span>
                        <span>{new Date(slip.uploadDate).toLocaleDateString('en-IN')}</span>
                        <span>•</span>
                        <Badge status={slip.status} />
                      </div>
                    </div>
                  </div>
                  <a
                    href={slip.fileUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="p-2 text-slate-600 hover:bg-slate-200 rounded transition flex-shrink-0"
                  >
                    <Download className="w-4 h-4" />
                  </a>
                </div>
              ))}
            </div>
          )}
        </Card>
      </div>
    </div>
  );
}
